"use client";

import { Logo } from "@/components/shared/logo";
import Link from "next/link";
import { useCallback } from "react";
import { usePathname } from "next/navigation";
import { Instagram, Linkedin } from "lucide-react";
import { PUBLIC_HEADER_OFFSET } from "@/app/(public)/[market]/_components/landing-snap-section";
import { scrollToElement } from "@/components/landing/scroll-to-element";

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function handleAnchorClick(id: string, e: React.MouseEvent) {
  if (window.location.pathname !== "/") return;
  e.preventDefault();
  setTimeout(() => {
    scrollToElement(id, PUBLIC_HEADER_OFFSET);
  }, 150);
}

export function Footer() {
  const pathname = usePathname();

  // Always render a Link. Branching the element type on usePathname() broke
  // hydration on the landing page: the prerendered HTML carried a <Link>
  // while the client tree rendered a <button> (#418, full-tree regeneration).
  // The onClick below preserves the home behaviour (scroll to top, clear hash).
  const handleLogoClick = useCallback(
    (e: React.MouseEvent) => {
      if (pathname === "/") {
        e.preventDefault();
        window.history.replaceState(null, "", "/");
        window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" });
      }
    },
    [pathname],
  );

  type FooterLink = { href: string; label: string; onClick?: (e: React.MouseEvent) => void };
  type LegalLink = { href: string; label: string };

  const mainLinks: FooterLink[] = [
    { href: "/#demo", label: "Demo", onClick: (e: React.MouseEvent) => handleAnchorClick("demo", e) },
    { href: "/#how-it-works", label: "How it works", onClick: (e: React.MouseEvent) => handleAnchorClick("how-it-works", e) },
    { href: "/#setup", label: "Setup", onClick: (e: React.MouseEvent) => handleAnchorClick("setup", e) },
    { href: "/#faq", label: "FAQ", onClick: (e: React.MouseEvent) => handleAnchorClick("faq", e) },
    { href: "/pricing", label: "Pricing", onClick: (e: React.MouseEvent) => { if (pathname === "/pricing") { e.preventDefault(); window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" }); } } },
  ];

  const featureLinks: FooterLink[] = [
    { href: "/follow-up-automation", label: "Follow-up automation" },
    { href: "/lost-reactivation", label: "Lost lead reactivation" },
    { href: "/google-reviews", label: "Google review system" },
    { href: "/contact-forms", label: "Contact forms" },
    { href: "/analytics", label: "Analytics" },
  ];

  const integrationLinks: FooterLink[] = [
    { href: "/integrations/simpro", label: "Simpro integration" },
    { href: "/integrations/servicem8", label: "ServiceM8 integration" },
    { href: "/integrations/tradify", label: "Tradify integration" },
    { href: "/integrations/fergus", label: "Fergus integration" },
  ];

  const resourceLinks: FooterLink[] = [
    { href: "/guides/price-estimator-placement", label: "Price estimator placement guide" },
    { href: "/guides/price-estimator-completion", label: "Price estimator completion guide" },
    { href: "/resources/strelve-and-job-management-software", label: "Strelve and job-management software" },
  ];

  const legalLinks: LegalLink[] = [
    { href: "/privacy", label: "Privacy" },
    { href: "/terms", label: "Terms" },
    { href: "/data-processing-addendum", label: "Data processing addendum" },
    { href: "/cookies", label: "Cookies" },
  ];

  return (
    <footer className="relative z-10 border-t border-canvas-border py-6 sm:py-8 landing-px bg-canvas">
      <div className="mx-auto w-full max-w-[1520px]">
        <div className="mb-8 flex items-center justify-between">
          <div className="shrink-0">
            <Link
              href="/"
              onClick={handleLogoClick}
              className="flex items-center group focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none rounded-sm min-h-[44px]"
              aria-label={
                pathname === "/" ? "Strelve: back to top" : "Strelve home"
              }
            >
              <Logo wordmarkOnly />
            </Link>
          </div>
          <Link
            href="/contact"
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-sm border border-canvas-border bg-canvas-surface px-5 py-2.5 font-mono text-xs text-canvas-fg transition-colors hover:border-canvas-muted hover:bg-canvas-hover focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none"
          >
            CONTACT US
          </Link>
        </div>

        <div className="mb-8 grid grid-cols-2 gap-x-6 gap-y-8 lg:grid-cols-3 xl:grid-cols-[repeat(6,max-content)] xl:justify-between">
          {/* Product */}
          <div className="text-left lg:col-span-1">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-canvas-muted-mid">
              Explore
            </p>
              <nav aria-label="Explore" className="flex flex-col gap-2 font-mono text-xs text-canvas-muted">
              {mainLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  onClick={link.onClick}
                  className="hover:text-canvas-fg transition-colors min-h-[44px] flex items-center focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none rounded-sm cursor-pointer"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Capabilities */}
          <div className="text-left lg:col-span-1">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-canvas-muted-mid">
              Capabilities
            </p>
            <nav aria-label="Capabilities" className="flex flex-col gap-2 font-mono text-xs text-canvas-muted">
              {featureLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  onClick={link.onClick}
                  className="hover:text-canvas-fg transition-colors min-h-[44px] flex items-center focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none rounded-sm cursor-pointer"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Integrations */}
          <div className="text-left">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-canvas-muted-mid">
              Integrations
            </p>
            <nav aria-label="Integrations" className="flex flex-col gap-2 font-mono text-xs text-canvas-muted">
              {integrationLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  className="hover:text-canvas-fg transition-colors min-h-[44px] flex items-center focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none rounded-sm"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Resources */}
          <div className="text-left">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-canvas-muted-mid">
              Resources
            </p>
            <nav aria-label="Resources" className="flex flex-col gap-2 font-mono text-xs text-canvas-muted">
              {resourceLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="hover:text-canvas-fg transition-colors min-h-[44px] flex items-center focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none rounded-sm"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Developers */}
          <div className="text-left">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-canvas-muted-mid">
              Developers
            </p>
            <nav aria-label="Developers" className="flex flex-col gap-2 font-mono text-xs text-canvas-muted">
              <Link
                href="/developers"
                className="hover:text-canvas-fg transition-colors min-h-[44px] flex items-center focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none rounded-sm"
              >
                Strelve API
              </Link>
            </nav>
          </div>

          {/* Legal */}
          <div className="text-left">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-canvas-muted-mid">
              Legal
            </p>
            <nav aria-label="Legal" className="flex flex-col gap-2 font-mono text-xs text-canvas-muted">
              {legalLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  className="flex min-h-[44px] items-center rounded-sm transition-colors hover:text-canvas-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

        </div>

        <div className="flex flex-col items-center gap-4 border-t border-canvas-border pt-6 sm:flex-row sm:justify-between">
          <p className="font-mono text-xs text-canvas-muted-mid">
            &copy; {new Date().getFullYear()} Strelve Limited. All rights
            reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link
              href="/resources"
              className="flex min-h-[44px] items-center rounded-sm font-mono text-xs text-canvas-muted transition-colors hover:text-canvas-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              All resources
            </Link>
            <a
              href="https://www.instagram.com/strelvehq/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Strelve on Instagram"
              className="text-canvas-muted hover:text-canvas-fg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none rounded-sm shrink-0"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a
              href="https://www.linkedin.com/company/strelve/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Strelve on LinkedIn"
              className="text-canvas-muted hover:text-canvas-fg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center focus-visible:ring-2 focus-visible:ring-primary/40 focus:outline-none rounded-sm shrink-0"
            >
              <Linkedin className="h-5 w-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
